document.documentElement.classList.add('js');

window.addEventListener('load', () => {
  if (!window.gsap || !window.SplitText) return;

  const headingSelector = `
    main h1, main h2, main h3, main h4, main h5, main h6,
    [role="main"] h1, [role="main"] h2, [role="main"] h3,
    [role="main"] h4, [role="main"] h5, [role="main"] h6
  `;

  const splitInstances = new WeakMap();
  const headingAnimations = new WeakMap();
  const observedHeadings = new WeakSet();

  function prefersReducedMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  function shouldSkipHeading(heading) {
    if (!heading) return true;

    if (heading.querySelector('a, button, [role="button"], input, select, textarea')) {
      return true;
    }

    return (
      heading.closest('header') ||
      heading.closest('.header') ||
      heading.closest('.shopify-section-header') ||
      heading.closest('.announcement-bar') ||
      heading.closest('footer') ||
      heading.closest('.footer') ||
      heading.closest('.menu-drawer') ||
      heading.closest('.drawer') ||
      heading.closest('.modal') ||
      heading.closest('.popup') ||
      heading.closest('.dialog') ||
      heading.closest('.accordion') ||
      heading.closest('.collapsible') ||
      heading.closest('.tabs') ||
      heading.closest('.slideshow') ||
      heading.closest('.slider') ||
      heading.closest('button') ||
      heading.closest('summary') ||
      heading.closest('a') ||
      heading.closest('[role="button"]') ||
      heading.closest('[aria-controls]') ||
      heading.closest('[aria-expanded]') ||
      heading.closest('[data-no-heading-animation]') ||
      heading.hasAttribute('data-no-heading-animation')
    );
  }

  /**
   * Keep headings hidden until SplitText has wrapped the text
   * and GSAP has applied the initial hidden state.
   */
  function hideHeadingUntilReady(heading) {
    heading.classList.add('gsap-heading-init');
    heading.classList.remove('gsap-heading-ready');
    heading.style.visibility = 'hidden';
    heading.style.opacity = '0';
  }

  /**
   * Reveal the heading only after SplitText + gsap.set() are complete.
   */
  function markHeadingVisible(heading) {
    heading.classList.add('gsap-heading-ready');
    heading.style.visibility = 'visible';
    heading.style.opacity = '1';
  }

  function destroySplit(heading) {
    const split = splitInstances.get(heading);
    if (!split) return;

    try {
      split.revert();
    } catch (error) {
      console.warn('SplitText revert failed:', error);
    }

    splitInstances.delete(heading);
  }

  function destroyHeadingAnimation(heading) {
    const animation = headingAnimations.get(heading);
    if (animation) {
      animation.kill();
      headingAnimations.delete(heading);
    }

    destroySplit(heading);

    heading.classList.remove('gsap-heading-init');
    heading.classList.remove('gsap-heading-ready');
    heading.removeAttribute('data-gsap-processed');
    heading.style.removeProperty('visibility');
    heading.style.removeProperty('opacity');
  }

  function animateHeading(heading) {
    if (!heading) return;
    if (heading.dataset.gsapProcessed === 'true' || heading.dataset.gsapProcessed === 'done') return;
    if (!heading.textContent || !heading.textContent.trim()) return;
    if (shouldSkipHeading(heading)) return;

    if (prefersReducedMotion()) {
      markHeadingVisible(heading);
      heading.dataset.gsapProcessed = 'done';
      return;
    }

    let split;

    try {
      /**
       * Important:
       * Split the text while the heading is still hidden.
       * This prevents the original text from flashing before wrapping finishes.
       */
      split = new SplitText(heading, {
        type: 'lines,words',
        linesClass: 'gsap-heading-line',
        wordsClass: 'gsap-heading-word'
      });
    } catch (error) {
      console.warn('SplitText failed:', error);
      markHeadingVisible(heading);
      heading.dataset.gsapProcessed = 'done';
      return;
    }

    if (!split || !split.words || !split.words.length) {
      markHeadingVisible(heading);
      heading.dataset.gsapProcessed = 'done';
      return;
    }

    splitInstances.set(heading, split);

    /**
     * Important:
     * Establish the hidden word state BEFORE making the heading visible.
     * This prevents double text and prevents words from animating over visible text.
     */
    gsap.set(split.words, {
      yPercent: 105,
      opacity: 0,
      willChange: 'transform, opacity'
    });

    /**
     * Reveal only after the split wrappers exist and the words are hidden.
     */
    markHeadingVisible(heading);

    const animation = gsap.to(split.words, {
      yPercent: 0,
      opacity: 1,
      duration: 0.9,
      stagger: 0.03,
      ease: 'power3.out',
      overwrite: 'auto',
      clearProps: 'transform,opacity,will-change',
      onComplete: () => {
        heading.dataset.gsapProcessed = 'done';
      }
    });

    headingAnimations.set(heading, animation);
    heading.dataset.gsapProcessed = 'true';
  }

  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;

        const heading = entry.target;
        animateHeading(heading);
        observer.unobserve(heading);
      });
    },
    {
      root: null,
      rootMargin: '0px 0px -15% 0px',
      threshold: 0.15
    }
  );

  function initHeadings(scope = document) {
    const headings = scope.querySelectorAll(headingSelector);
    if (!headings.length) return;

    headings.forEach((heading) => {
      if (heading.dataset.gsapProcessed === 'done' || heading.dataset.gsapProcessed === 'true') return;
      if (shouldSkipHeading(heading)) return;
      if (observedHeadings.has(heading)) return;

      if (prefersReducedMotion()) {
        markHeadingVisible(heading);
        heading.dataset.gsapProcessed = 'done';
        return;
      }

      /**
       * Important:
       * Hide headings during init so they cannot flash before the observer fires.
       */
      hideHeadingUntilReady(heading);

      observedHeadings.add(heading);
      observer.observe(heading);
    });
  }

  function initNewShopifySection(event) {
    if (!event.target) return;
    initHeadings(event.target);
  }

  initHeadings(document);

  document.addEventListener('shopify:section:load', initNewShopifySection);

  if (document.fonts && document.fonts.ready) {
    document.fonts.ready.then(() => {
      initHeadings(document);
    });
  }
});
